// E2E-encrypted contact-alias blob (docs/08 §8.1). The alias map is the
// observer's PRIVATE nicknames for friends; it syncs across an account's
// devices only as ciphertext the server can never read. Same cipher/layout
// family as `encryptIdentitySeed` (identity/kdf.ts) — XChaCha20-Poly1305 over a
// 24-byte random nonce — plus a leading version byte so the format can evolve.
//
// Deliberately WASM-free (like kdf.ts): the headless brain imports this and must
// never drag in hash-wasm. Domain-separated from every other seed derivation via
// SIG_DOMAIN.ALIAS_ENC (docs/03).

import { xchacha20poly1305 } from '@noble/ciphers/chacha.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { SIG_DOMAIN } from '@merkury/protocol';
import { randomBytes, utf8 } from '../lib/bytes.js';
import { sanitizeAlias } from '../storage/aliases.js';

/** Blob version byte — bumped only on a layout/cipher change. */
const VERSION = 0x01;
const NONCE_LENGTH = 24;
/** Poly1305 tag — the ciphertext of an empty plaintext is exactly this. */
const TAG_LENGTH = 16;

/**
 * `aliasEncKey = HKDF-SHA256(ikm = seed, salt = none, info = ALIAS_ENC, 32)`
 * (docs/08 §8.1). Derivable on any device that holds the identity seed (phrase
 * or passkey restore); a distinct HKDF branch from every other seed derivation.
 */
export function deriveAliasKey(seed: Uint8Array): Uint8Array {
  return hkdf(sha256, seed, undefined, utf8(SIG_DOMAIN.ALIAS_ENC), 32);
}

/**
 * Canonical UTF-8 JSON of the alias map with keys sorted — so the same map
 * always encrypts to the same plaintext (deterministic, testable), independent
 * of insertion order.
 */
function canonicalJson(map: Record<string, string>): string {
  const sorted: Record<string, string> = {};
  for (const key of Object.keys(map).sort()) sorted[key] = map[key]!;
  return JSON.stringify(sorted);
}

/**
 * Encrypt the alias map into the wire blob:
 * `0x01 ‖ 24-byte nonce ‖ XChaCha20-Poly1305(key, canonical-JSON)` (docs/08 §8.1).
 */
export function encryptAliases(key: Uint8Array, map: Record<string, string>): Uint8Array {
  const plaintext = utf8(canonicalJson(map));
  const nonce = randomBytes(NONCE_LENGTH);
  const ciphertext = xchacha20poly1305(key, nonce).encrypt(plaintext);
  const out = new Uint8Array(1 + NONCE_LENGTH + ciphertext.length);
  out[0] = VERSION;
  out.set(nonce, 1);
  out.set(ciphertext, 1 + NONCE_LENGTH);
  return out;
}

/**
 * Decrypt a blob back to an alias map, or `null` (NEVER throws) on any failure:
 * unknown/missing version byte, truncated blob, Poly1305 tag mismatch (wrong key
 * or tampering), non-JSON plaintext, or a payload that isn't a flat string map.
 * Surviving entries are validated through `sanitizeAlias` (docs/08 §3); entries
 * that sanitize to empty are dropped, so a corrupt-but-authentic blob can't
 * inject an invalid alias.
 */
export function decryptAliases(key: Uint8Array, blob: Uint8Array): Record<string, string> | null {
  if (blob.length < 1 + NONCE_LENGTH + TAG_LENGTH) return null;
  if (blob[0] !== VERSION) return null;
  const nonce = blob.subarray(1, 1 + NONCE_LENGTH);
  const ciphertext = blob.subarray(1 + NONCE_LENGTH);
  let plaintext: Uint8Array;
  try {
    plaintext = xchacha20poly1305(key, nonce).decrypt(ciphertext);
  } catch {
    return null; // bad tag: wrong key or tampered blob
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(new TextDecoder().decode(plaintext));
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const out: Record<string, string> = {};
  for (const [userId, value] of Object.entries(parsed as Record<string, unknown>)) {
    if (typeof value !== 'string') return null; // not a flat string map
    const clean = sanitizeAlias(value);
    if (clean.length > 0) out[userId] = clean;
  }
  return out;
}
